import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonContent,
  IonButton,
  IonAlert,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardContent,
  IonIcon,
} from '@ionic/react';
import { chevronForward, trashOutline } from 'ionicons/icons';
import { routineRepository } from '../../../repositories/LocalRepositories';
import { useAuth } from '../../../auth';
import { useHistory } from 'react-router-dom';
import { Routine } from '../../../datamodels';

import './RoutineList.css';

const RoutineList: React.FC = () => {
  const { userId } = useAuth();
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const history = useHistory();

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    routineRepository.list(userId).then(rows => { if (!cancelled) setRoutines(rows); }).catch(error => console.error('Error fetching routines:', error));
    return () => { cancelled = true; };
  }, [userId]);

  const formatTimestamp = (timestamp?: {
    seconds: number;
    nanoseconds: number;
  }) => {
    if (!timestamp) return 'Unknown Date';
    return new Date(timestamp.seconds * 1000).toLocaleString();
  };

  const handleDelete = async (routineId: string) => {
    try {
      await routineRepository.delete(routineId);
      setRoutines(current => current.filter(routine => routine.id !== routineId));
    } catch (error) {
      console.error('Error deleting routine:', error);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/my/training" text="" />
          </IonButtons>
          <IonTitle>My Routines</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="routines-content">
        <section className="routines-intro" aria-labelledby="routines-intro-title">
          <div className="routines-intro__mascot" aria-hidden="true">
            <img src="/assets/ploppy/ploppy.png" alt="" />
          </div>
          <div className="routines-intro__copy">
            <span>Your workout shortcuts</span>
            <h1 id="routines-intro-title">What are routines?</h1>
            <p>
              Save your favourite machines, exercises and sets, then start the
              same workout again with one tap.
            </p>
          </div>
        </section>

        {routines.length === 0 && (
          <div className="routines-empty">
            <strong>No routines yet</strong>
            <span>Finish building a workout and save it as a routine.</span>
          </div>
        )}

        <div className="routines-list" aria-label="Saved routines">
          {routines.map(routine => (
            <IonCard
              key={routine.id}
              className="routine-card"
              role="button"
              tabIndex={0}
              onClick={() => history.push(`/my/routines/${routine.id}`)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  history.push(`/my/routines/${routine.id}`);
                }
              }}
            >
              <IonCardContent className="routine-card__content">
                <div className="routine-card__body">
                  <h2>{routine.name}</h2>
                  <p>
                    {routine.machineSessions.length}{' '}
                    {routine.machineSessions.length === 1 ? 'exercise' : 'exercises'}
                    <span aria-hidden="true"> · </span>
                    {formatTimestamp(routine.created_at)}
                  </p>
                </div>

                <IonButton
                  fill="clear"
                  color="danger"
                  className="routine-card__delete"
                  aria-label={`Delete ${routine.name}`}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setConfirmDeleteId(routine.id!);
                  }}
                >
                  <IonIcon icon={trashOutline} slot="icon-only" />
                </IonButton>

                <IonIcon
                  icon={chevronForward}
                  className="routine-card__chevron"
                  aria-hidden="true"
                />
              </IonCardContent>
            </IonCard>
          ))}
        </div>

        {/* Confirmation Alert for Deletion */}
        <IonAlert
          isOpen={!!confirmDeleteId}
          onDidDismiss={() => setConfirmDeleteId(null)}
          header="Confirm Delete"
          message="Are you sure you want to delete this routine?"
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => setConfirmDeleteId(null),
            },
            {
              text: 'Delete',
              handler: () => {
                if (confirmDeleteId) {
                  handleDelete(confirmDeleteId);
                  setConfirmDeleteId(null);
                }
              },
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default RoutineList;
